import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { EmployeeLoan } from '@pms/shared';
import { payrollService } from '@/services/payroll.service';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

function formatCurrency(val: string | number): string {
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatMonthYear(year: number, month: number): string {
  return new Date(year, month - 1).toLocaleString('en-IN', { month: 'long', year: 'numeric' });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

interface HistoryRow {
  id: string;
  year: number;
  month: number;
  amount: string;
  date: string;
  by: string;
}

const PAGE_SIZE = 10;

interface Props {
  loan: EmployeeLoan;
  onClose: () => void;
}

export function LoanHistoryModal({ loan, onClose }: Props) {
  const [page, setPage] = useState(1);

  const { data: payments = [], isLoading } = useQuery({
    queryKey: ['loan-payments', loan.id],
    queryFn: () => payrollService.getLoanPayments(loan.id),
  });

  const rows: HistoryRow[] = payments.map((p) => ({
    id: p.id,
    year: p.year,
    month: p.month,
    amount: p.amount_paid,
    date: formatDate(p.paid_at),
    by: [p.paid_by_first, p.paid_by_last].filter(Boolean).join(' ') || '—',
  }));

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const totalAmount = parseFloat(loan.amount);
  const remaining = parseFloat(loan.remaining_amount ?? loan.amount);
  const paid = totalAmount - remaining;
  const employeeName = [loan.first_name, loan.last_name].filter(Boolean).join(' ') || '—';

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-base">Loan History</DialogTitle>
        </DialogHeader>

        {/* Summary */}
        <div className="rounded-lg bg-muted/50 px-4 py-3 space-y-2">
          <p className="text-sm font-medium">
            Employee: <span className="font-semibold">{employeeName}</span>
            <span className="ml-3 text-xs text-muted-foreground font-normal">
              {loan.emis_paid ?? 0}/{loan.num_emis} EMIs · Status: <span className="capitalize">{loan.status}</span>
            </span>
          </p>
          <div className="grid grid-cols-4 gap-3 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Total Loan</p>
              <p className="font-semibold">{formatCurrency(loan.amount)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">EMI Amount</p>
              <p className="font-semibold">{formatCurrency(loan.emi_amount)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total Paid</p>
              <p className="font-semibold text-emerald-600">{formatCurrency(paid)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Remaining</p>
              <p className={`font-semibold ${remaining > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                {formatCurrency(remaining)}
              </p>
            </div>
          </div>
        </div>


        {/* Table */}
        <div className="rounded-md border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-primary text-primary-foreground">
                <th className="px-4 py-2 text-left text-xs font-semibold">Month</th>
                <th className="px-4 py-2 text-right text-xs font-semibold">Amount Paid</th>
                <th className="px-4 py-2 text-right text-xs font-semibold">Date</th>
                <th className="px-4 py-2 text-right text-xs font-semibold">By</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-sm text-muted-foreground">Loading…</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-sm text-muted-foreground">No payments recorded yet</td>
                </tr>
              ) : (
                pageRows.map((r) => (
                  <tr key={r.id} className="border-t border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-2.5">{formatMonthYear(r.year, r.month)}</td>
                    <td className="px-4 py-2.5 text-right font-medium text-emerald-600">{formatCurrency(r.amount)}</td>
                    <td className="px-4 py-2.5 text-right text-muted-foreground">{r.date}</td>
                    <td className="px-4 py-2.5 text-right text-muted-foreground">{r.by}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination + footer */}
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            {rows.length === 0 ? '0 entries' : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, rows.length)} of ${rows.length}`}
          </p>
          <div className="flex items-center gap-2">
            {totalPages > 1 && (
              <>
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="rounded p-1 hover:bg-muted disabled:opacity-40"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-xs text-muted-foreground">{page} / {totalPages}</span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="rounded p-1 hover:bg-muted disabled:opacity-40"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </>
            )}
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
